import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { globalSearch, SearchResult } from '../utils/globalSearch';
import { useLanguageNavigate } from '../hooks/useLanguageNavigate';
import { Sidebar, MobileHeader } from './dashboard';

export function SearchResultsPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const langNavigate = useLanguageNavigate();
  const [searchParams] = useSearchParams();
  const searchTerm = searchParams.get('q') || '';
  const isRTL = i18n.language === 'he';
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [user, setUser] = useState<any>(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Load user from localStorage
  useEffect(() => {
    const savedUser = localStorage.getItem('user');
    if (savedUser) {
      try {
        const userData = JSON.parse(savedUser);
        setUser(userData);
      } catch (err) {
        console.error('Error parsing user:', err);
      }
    }
  }, []);

  // Perform search when search term changes
  useEffect(() => {
    const performSearch = async () => {
      if (!searchTerm || searchTerm.trim().length < 2) {
        setSearchResults([]);
        return;
      }

      setIsSearching(true);
      try {
        const results = await globalSearch(searchTerm);
        setSearchResults(results);
      } catch (error) {
        console.error('Error performing global search:', error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    };

    performSearch();
  }, [searchTerm]);

  const handleSelectResult = (result: SearchResult) => {
    switch (result.type) {
      case 'patient':
        if (result.caseId) {
          langNavigate(`/patient/${result.caseId}`);
        }
        break;
      case 'event':
        if (result.caseId) {
          langNavigate(`/patient/${result.caseId}?event=${result.id}`);
        }
        break;
      case 'task':
        if (result.caseId) {
          langNavigate(`/patient/${result.caseId}?task=${result.id}`);
        }
        break;
      case 'milestone':
        if (result.caseId) {
          langNavigate(`/patient/${result.caseId}?milestone=${result.id}`);
        }
        break;
      case 'form':
        // Forms don't have navigation yet
        break;
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'patient': return t('search.patient') || 'Patient';
      case 'event': return t('search.event') || 'Meeting';
      case 'task': return t('search.task') || 'Task';
      case 'form': return t('search.form') || 'Intake Form';
      case 'milestone': return t('search.milestone') || 'Milestone';
      default: return type;
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'patient': return '👤';
      case 'event': return '📅';
      case 'task': return '✅';
      case 'form': return '📋';
      case 'milestone': return '🎯';
      default: return '📄';
    }
  };

  const groupedResults = searchResults.reduce((acc, result) => {
    if (!acc[result.type]) {
      acc[result.type] = [];
    }
    acc[result.type].push(result);
    return acc;
  }, {} as Record<string, SearchResult[]>);

  if (!user) {
    return <div>Loading...</div>;
  }

  return (
    <div className="app dashboard-mode">
      <MobileHeader
        user={user}
        isMobileMenuOpen={isMobileMenuOpen}
        onToggleMenu={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
      />

      <div className="dashboard-container">
        <div className="dashboard">
          {/* Mobile Menu Overlay */}
          {isMobileMenuOpen && (
            <div 
              className="mobile-menu-overlay"
              onClick={() => setIsMobileMenuOpen(false)}
            ></div>
          )}
          
          <Sidebar
            user={user}
            activeTab=""
            isMobileMenuOpen={isMobileMenuOpen}
            onTabChange={(tab) => {
              if (tab === 'dashboard') {
                navigate('/dashboard');
              } else {
                navigate(`/dashboard?tab=${tab}`);
              }
            }}
            onSignOut={() => navigate('/')}
            onCloseMobileMenu={() => setIsMobileMenuOpen(false)}
          />

          <div className="main-content" style={{ marginLeft: '280px', padding: '20px' }}>
            <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
              {/* Header */}
              <div style={{ marginBottom: '24px' }}>
                <button
                  onClick={() => navigate(-1)}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: '#007acc',
                    cursor: 'pointer',
                    fontSize: '14px',
                    marginBottom: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  ← {t('common.back') || 'Back'}
                </button>
                <h1 style={{ fontSize: '24px', fontWeight: '600', margin: 0, color: '#000' }}>
                  {t('search.results') || 'Search Results'}
                </h1>
                <p style={{ color: '#666', marginTop: '8px', fontSize: '14px' }}>
                  {searchTerm ? (
                    <>
                      {t('search.searchingFor') || 'Searching for'}: <strong>"{searchTerm}"</strong>
                      {searchResults.length > 0 && (
                        <> - {searchResults.length} {t('search.resultsFound') || 'results found'}</>
                      )}
                    </>
                  ) : (
                    t('search.noSearchTerm') || 'No search term provided'
                  )}
                </p>
              </div>

              {/* Search Results */}
              {isSearching ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                  {t('common.loading') || 'Searching...'}
                </div>
              ) : searchResults.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                  <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔍</div>
                  <h3 style={{ color: '#000', marginBottom: '8px' }}>
                    {t('search.noResults') || 'No results found'}
                  </h3>
                  <p>{t('search.tryDifferentTerm') || 'Try a different search term'}</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                  {Object.entries(groupedResults).map(([type, typeResults]) => (
                    <div key={type} style={{ marginBottom: '24px' }}>
                      <div style={{
                        fontSize: '16px',
                        fontWeight: '600',
                        color: '#333',
                        marginBottom: '12px',
                        paddingBottom: '8px',
                        borderBottom: '2px solid #e9ecef'
                      }}>
                        {getTypeIcon(type)} {getTypeLabel(type)} ({typeResults.length})
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {typeResults.map((result) => (
                          <div
                            key={result.id}
                            onClick={() => handleSelectResult(result)}
                            style={{
                              padding: '16px',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              backgroundColor: 'white',
                              border: '1px solid #e9ecef',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = '#f8f9fa';
                              e.currentTarget.style.borderColor = '#007acc';
                              e.currentTarget.style.transform = 'translateY(-2px)';
                              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = 'white';
                              e.currentTarget.style.borderColor = '#e9ecef';
                              e.currentTarget.style.transform = 'translateY(0)';
                              e.currentTarget.style.boxShadow = 'none';
                            }}
                          >
                            <div style={{ fontWeight: '500', marginBottom: '4px', color: '#000', fontSize: '16px' }}>
                              {result.title}
                            </div>
                            {result.subtitle && (
                              <div style={{ fontSize: '13px', color: '#666' }}>
                                {result.subtitle}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

